import { useState } from "react";
import { toast } from "sonner";
import { Pencil, Trash2 } from "lucide-react";
import { api, errMsg, money } from "@/lib/api";
import { FillBar } from "@/components/Bits";
import HigherDropFormDialog from "@/components/admin/HigherDropFormDialog";

const SetFill = ({ d }) => (
  <div data-testid={`higher-drop-fill-${d.id}`} className="basis-full grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-4 gap-y-2 pt-2">
    {d.fill.map((f, i) => (
      <div key={f.name} className="min-w-0">
        <div className={`text-xs truncate ${f.assigned >= f.available ? "text-emerald-300" : "text-zinc-300"}`}>{f.name}{f.assigned >= f.available ? " · Full" : ""}</div>
        <FillBar filled={f.assigned} total={f.available} unit="PCs" testId={`higher-set-fill-${d.id}-${i}`} />
      </div>
    ))}
  </div>
);

const DropRow = ({ d, onEdit, onDelete }) => (
  <div data-testid={`higher-drop-row-${d.id}`} className="flex flex-wrap items-center gap-x-4 gap-y-1.5 rounded-xl border border-white/5 bg-zinc-900/60 px-4 py-3">
    <div className="min-w-0 flex-1 basis-full sm:basis-auto">
      <div className="font-semibold truncate">{d.name}</div>
      <div className="text-xs text-zinc-400 truncate">{(d.sets || []).map((s) => `${s.name} ×${s.count}`).join(" · ")}</div>
    </div>
    <span className="font-mono text-xs text-zinc-400">{d.num_sets} sets · {d.total_pcs}/{d.total_available} PCs</span>
    {d.missing_items && <span data-testid={`higher-drop-missing-${d.id}`} className="rounded-full border border-red-500/40 bg-red-500/10 px-2 py-0.5 text-[11px] font-bold text-red-300">{d.missing_sets} missing</span>}
    <span className="font-mono text-xs text-sky-300">{d.has_ems ? `EMS ${money(d.ems)}/PC` : "No EMS"}</span>
    <button type="button" data-testid={`higher-drop-edit-${d.id}`} onClick={() => onEdit(d)} className="rounded-lg p-1.5 text-zinc-400 transition-colors hover:bg-white/5 hover:text-white"><Pencil className="h-4 w-4" /></button>
    <button type="button" data-testid={`higher-drop-delete-${d.id}`} onClick={() => onDelete(d)} className="rounded-lg p-1.5 text-zinc-400 transition-colors hover:bg-red-500/10 hover:text-red-300"><Trash2 className="h-4 w-4" /></button>
    <SetFill d={d} />
  </div>
);

export default function HigherDropsAdmin({ drops, onChanged }) {
  const [editing, setEditing] = useState(null);
  const del = async (d) => {
    if (!window.confirm(`Delete ${d.name}? All PCs assigned in this drop will be removed.`)) return;
    try {
      await api.delete(`/higher/drops/${d.id}`);
      toast.success("Higher Drop deleted");
      onChanged();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <section className="mt-12" data-testid="higher-drops-admin">
      <h2 className="font-display text-base md:text-lg font-bold">Higher Drops <span className="font-mono text-sm text-zinc-500">({drops.length})</span></h2>
      <p className="text-xs text-zinc-500 mt-1">Admin only. Assign PCs from each Higher Joiner's page.</p>
      <div className="mt-3 space-y-2">
        {drops.length === 0 && <div data-testid="higher-drops-empty" className="rounded-xl border border-dashed border-zinc-800 px-4 py-5 text-sm text-zinc-500">No Higher Drops yet. Add one from Admin → Yani - Higher.</div>}
        {drops.map((d) => <DropRow key={d.id} d={d} onEdit={setEditing} onDelete={del} />)}
      </div>
      {editing && <HigherDropFormDialog d={editing} onClose={() => setEditing(null)} onSaved={onChanged} />}
    </section>
  );
}
